import { useId, type ReactNode } from 'react';

import { ApiError } from '@shared/api/client';
import type { ErrorCopy } from '@shared/errors/catalog';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatNumber } from '@shared/format';
import { ErrorBanner } from '@shared/ui/ErrorBanner';

import { sharedCopy } from './copy';

/**
 * The two fragments all three subscription surfaces render the same way.
 *
 * They live here rather than in one page because a second page imports each of
 * them — the rule of extraction, not a preference for a components bag.
 */

/**
 * How many rows are shown against how many exist.
 *
 * **Not an empty state and not a pager.** No catalog row gives these surfaces a
 * traversal control, so the surface states its own truncation instead of
 * inventing one — and it states it even when the two figures are equal, because
 * a line that appears only on truncation teaches the operator to read its
 * absence as completeness on a page where it simply was not rendered.
 */
export function TruncationLine({
  shown,
  total,
  children,
}: {
  shown: number;
  total: number;
  /** The rest of the sentence: where the full record is read, and how. */
  children?: ReactNode;
}) {
  return (
    <p className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">
      {sharedCopy.truncation.prefix} <bdi>{formatNumber(shown)}</bdi> {sharedCopy.truncation.middle}{' '}
      <bdi>{formatNumber(total)}</bdi>
      {children ? <> — {children}</> : null}
    </p>
  );
}

/**
 * One failed read, rendered against its own band.
 *
 * A panel-level failure never blanks a page: each surface composes its reads
 * independently and renders this beside the ones that landed. The tone comes
 * from the resolved copy rather than from the call site — a `502` is neutral
 * because the failure is upstream and nobody in this console caused it.
 */
export function SurfaceError({
  error,
  catalog,
  onRetry,
}: {
  error: unknown;
  catalog: Readonly<Record<string, ErrorCopy>>;
  onRetry: () => void;
}) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const resolved = resolveErrorCopy(code, status, catalog);

  return (
    <ErrorBanner
      message={resolved.message}
      tone={resolved.tone === 'danger' ? 'danger' : 'neutral'}
      onRetry={onRetry}
    />
  );
}

/**
 * A labelled control with its hint and its error, used by both record dialogs in
 * this area.
 *
 * **The error joins `aria-describedby` when it appears and leaves when it goes**,
 * so no hidden text is announced — a permanent association pointing at an empty
 * node reads as a field that always has something wrong with it.
 *
 * The control itself is a render prop rather than a set of props, because the
 * three inputs differ in type, direction, and mode, and a component that grew a
 * flag per difference would be a form library nobody asked for.
 */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: (id: string, describedBy: string | undefined, invalid: boolean) => ReactNode;
}) {
  const id = useId();
  const hintId = useId();
  const errorId = useId();

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="grid gap-2">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>

      {children(id, describedBy, Boolean(error))}

      {hint ? (
        <p id={hintId} className="mb-0 max-w-none text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="mb-0 max-w-none text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** The text-input shape both record dialogs use, so neither drifts from the other. */
export const FIELD_CONTROL_CLASS =
  'hit-target w-full rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink';
